import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import axiosInstance from "../utils/axiosConfig";
import { notifyToast } from "../utils/notify";
import { PrimaryButton } from "./button";
import { useSettings } from "../hooks/useSettings";
import SwipeableDrawerComponent from "./SwipeableDrawerComponent ";

/* ─── tiny helpers ─────────────────────────────────────────── */

const Label = ({ children }) => (
  <Typography
    sx={{
      fontFamily: "Proxima Nova",
      fontSize: "12px",
      fontWeight: 600,
      color: "#6F747E",
      lineHeight: "16px",
      letterSpacing: "0.24px",
      textTransform: "uppercase",
      mb: "2px",
    }}
  >
    {children}
  </Typography>
);

const Value = ({ children }) => (
  <Typography
    sx={{
      fontFamily: "Proxima Nova",
      fontSize: "14px",
      fontWeight: 400,
      color: "#1A1C1E",
      lineHeight: "20px",
    }}
  >
    {children || "--"}
  </Typography>
);

const Field = ({ label, value, canCopy }) => (
  <Box sx={{ mb: "16px" }}>
    <Label>{label}</Label>
    <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
      <Value>{value}</Value>
      {canCopy && value && (
        <Box
          component="span"
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            color: "#6F747E",
            "&:hover": { color: "#1A1C1E" },
          }}
          onClick={() => {
            navigator.clipboard.writeText(value);
            notifyToast.success("Success", `${label} copied to clipboard!`);
          }}
        >
          <ContentCopyIcon sx={{ fontSize: "14px" }} />
        </Box>
      )}
    </Box>
  </Box>
);

const SectionHeading = ({ children }) => (
  <Typography
    sx={{
      fontFamily: "Proxima Nova",
      fontSize: "13px",
      fontWeight: 700,
      color: "#043A87",
      letterSpacing: "0.52px",
      textTransform: "uppercase",
    }}
  >
    {children}
  </Typography>
);

const CustomAccordion = ({ title, defaultExpanded, children }) => (
  <Accordion
    defaultExpanded={defaultExpanded}
    disableGutters
    elevation={0}
    sx={{
      border: "1px solid #E0E2E7",
      borderRadius: "8px !important",
      mb: "16px",
      "&:before": { display: "none" },
    }}
  >
    <AccordionSummary
      expandIcon={<ExpandMoreIcon />}
      sx={{
        background: "#F9FAFB",
        borderBottom: "1px solid #E0E2E7",
        minHeight: "48px",
        "& .MuiAccordionSummary-content": { my: "12px" },
      }}
    >
      <SectionHeading>{title}</SectionHeading>
    </AccordionSummary>
    <AccordionDetails sx={{ p: "16px 24px" }}>
      {children}
    </AccordionDetails>
  </Accordion>
);

/* ─── status chip ─────────────────────────────────────────── */
const InsuranceChip = ({ status }) => {
  let color = "#6F747E";
  let bg = "rgba(111,116,126,0.10)";
  if (status === "BOUND") { color = "#009D0F"; bg = "rgba(0,157,15,0.09)"; }
  else if (status === "FAILED") { color = "#F1554C"; bg = "rgba(241,85,76,0.08)"; }
  else if (status === "QUOTED" || status === "PENDING_QUOTE") { color = "#F98244"; bg = "rgba(249,130,68,0.09)"; }

  return (
    <Typography
      component="span"
      sx={{
        color,
        background: bg,
        borderRadius: "40px",
        padding: "3px 10px",
        fontFamily: "Proxima Nova",
        fontSize: "13px",
        fontWeight: 500,
        textTransform: "capitalize",
      }}
    >
      {status ? (status === "BOUND" ? "applied" : status.replace(/_/g, " ").toLowerCase()) : "--"}
    </Typography>
  );
};

/* ─── main component ──────────────────────────────────────── */
const InsuranceDetailModal = ({ open, policyId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [policy, setPolicy] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const { dateformat, timezone, formatDate, timeFormat } = useSettings();

  useEffect(() => {
    if (open && policyId) {
      fetchPolicyDetails();
    } else {
      setPolicy(null);
    }
  }, [open, policyId]);

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      const response = await axiosInstance.post(`/crm/insurance/policies/${policyId}/retry/`, {});
      notifyToast.success("Success", response?.data?.message || "Retry initiated successfully.");
      fetchPolicyDetails();
    } catch (error) {
      notifyToast.error("Error", error?.response?.data?.message || "Retry failed.");
    } finally {
      setIsRetrying(false);
    }
  };

  const fetchPolicyDetails = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/crm/insurance/policies/${policyId}/`);
      setPolicy(res?.data?.data || null);
    } catch (err) {
      notifyToast.error("Error", err?.response?.data?.message || "Failed to load policy details");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const fmt = (dt) =>
    dt ? `${formatDate(dt, timezone, dateformat)}  ${formatDate(dt, timezone, timeFormat)}` : null;

  return (
    <SwipeableDrawerComponent isOpen={open} anchor="right" onClose={onClose}>
      <Box
        sx={{
          width: "420px",
          background: "#FFF",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderLeft: "1px solid #E0E0E0",
        }}
      >
        {/* ── header ── */}
        <Box
          sx={{
            padding: "12px 24px",
            borderBottom: "1px solid #E0E0E0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <Box>
            <Typography
              sx={{
                color: "#1A1C1E",
                fontFamily: "Proxima Nova",
                fontWeight: 600,
                fontSize: "18px",
                lineHeight: "24px",
              }}
            >
              Insurance Details
            </Typography>
            {policy && (
              <Typography
                sx={{
                  fontFamily: "Proxima Nova",
                  fontSize: "13px",
                  color: "#6F747E",
                  mt: "2px",
                }}
              >
                {policy.booking_id || ""}
              </Typography>
            )}
          </Box>
          <Button
            sx={{
              minWidth: "32px",
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              color: "#6F747E",
              padding: 0,
              "&:hover": { backgroundColor: "rgba(0,0,0,0.04)", color: "#000" },
            }}
            onClick={onClose}
          >
            <CloseIcon fontSize="small" />
          </Button>
        </Box>

        {/* ── body ── */}
        <Box sx={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="200px">
              <CircularProgress size={28} sx={{ color: "#043A87" }} />
            </Box>
          ) : !policy ? (
            <Typography color="textSecondary">No details found.</Typography>
          ) : (
            <>
              {/* Status banner */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: "12px 16px",
                  borderRadius: "8px",
                  background: "#F9FAFB",
                  border: "1px solid #E0E2E7",
                  mb: "20px",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <InsuranceChip status={policy.status} />
                  <Typography sx={{ fontFamily: "Proxima Nova", fontSize: "13px", color: "#6F747E" }}>
                    {policy.external_reference_number || ""}
                  </Typography>
                </Box>

                {["FAILED", "QUOTED", "PENDING_QUOTE"].includes(policy.status) && (
                  <PrimaryButton
                    disabled={isRetrying}
                    sx={{
                      opacity: isRetrying ? 0.6 : 1,
                      minWidth: "70px",
                      py: "4px",
                      height: "24px",
                    }}
                    onClick={handleRetry}
                  >
                    {isRetrying ? "Retrying…" : "Retry"}
                  </PrimaryButton>
                )}
              </Box>

              {/* Policy Info */}
              <CustomAccordion title="Policy Information" defaultExpanded={true}>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
                  <Field label="Quote ID" value={policy.quote_id} canCopy />
                  <Field label="Premium (Rider)" value={policy.premium_charged_to_rider ? `${policy.currency || ""} ${policy.premium_charged_to_rider}` : null} />
                  <Field label="Owed to Stere" value={policy.premium_owed_to_stere ? `${policy.currency || ""} ${policy.premium_owed_to_stere}` : null} />
                  <Field label="Platform Fee" value={policy.platform_fee ? `${policy.currency || ""} ${policy.platform_fee}` : null} />
                  <Field label="Coverage Amount" value={policy.coverage_amount ? `${policy.currency || ""} ${policy.coverage_amount}` : null} />
                  <Field label="Created At" value={fmt(policy.created_at)} />
                </Box>
              </CustomAccordion>

              {/* Customer Info */}
              <CustomAccordion title="Customer">
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
                  <Field label="Name" value={policy.rider?.full_name} />
                  <Field label="Phone" value={policy.rider?.phone} />
                  <Field label="Customer ID" value={policy.rider?.id} />
                  <Field label="Email" value={policy.rider?.email} />
                </Box>
              </CustomAccordion>

              {/* Driver Info */}
              <CustomAccordion title="Driver">
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
                  <Field label="Name" value={policy.driver?.full_name} />
                  <Field label="Phone" value={policy.driver?.phone} />
                </Box>
              </CustomAccordion>

              {/* Ride Info */}
              <CustomAccordion title="Ride">
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
                  <Field label="Status" value={policy.ride?.status?.replace(/_/g, " ")} />
                  <Field label="Cab Type" value={policy.ride?.cab_type} />
                  <Field label="Start Time" value={fmt(policy.ride?.ride_start_time)} />
                  <Field label="End Time" value={fmt(policy.ride?.ride_end_time)} />
                </Box>
              </CustomAccordion>
            </>
          )}
        </Box>
      </Box>
    </SwipeableDrawerComponent>
  );
};

export default InsuranceDetailModal;
